import { RefObject, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";
import { z } from "zod";

import { ModalRef } from "@shared/components";
import { listMapIntegrationPlansQueryKey } from "@modules/mapIntegrationPlans/services/queries/listMapIntegrationPlans/query";
import { createMapIntegrationPlanService } from "@modules/mapIntegrationPlans/services/mutations/createMapIntegrationPlan/service";

// Schema que aceita tanto plan (único) quanto plans (múltiplos)
const createMapIntegrationPlanFormSchema = (translate: (key: string) => string) => {
  return z.object({
    plan: z.number().min(1).optional().nullable(),
    plans: z.array(z.number().min(1)).optional().nullable(),
    plan_slug_integration: z.string().optional(),
    plan_slug_integration_list: z.string().optional(),
    price_integration: z.any().optional().nullable(),
    price_from: z.preprocess((v) => (v === "" || v == null ? null : Number(v)), z.number().min(0).nullable().optional()),
    price_to: z.preprocess((v) => (v === "" || v == null ? null : Number(v)), z.number().min(0).nullable().optional()),
  }).refine(
    (data) => {
      const hasPlan = !!data.plan;
      const hasPlans = !!data.plans && Array.isArray(data.plans) && data.plans.length > 0;
      return hasPlan || hasPlans;
    },
    {
      message: "Selecione pelo menos um plano",
      path: ["plans"]
    }
  ).refine(
    (data) => {
      const hasName = !!data.plan_slug_integration && data.plan_slug_integration.trim().length > 0;
      const hasList = !!data.plan_slug_integration_list && data.plan_slug_integration_list.trim().length > 0;
      return hasName || hasList;
    },
    {
      message: "Informe o nome do plano ou a lista de planos do parceiro",
      path: ["plan_slug_integration"]
    }
  );
};

type CreateMapIntegrationPlanFormValues = z.infer<ReturnType<typeof createMapIntegrationPlanFormSchema>>;

export function useCreateMapIntegrationPlanModal(modalRef: RefObject<ModalRef>) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const formMethods = useForm<CreateMapIntegrationPlanFormValues>({
    resolver: zodResolver(createMapIntegrationPlanFormSchema(translate)),
    mode: "onBlur",
    defaultValues: {
      plan: undefined,
      plans: undefined,
      plan_slug_integration: "",
      plan_slug_integration_list: "",
    },
  });

  // Criar um reset customizado que garante que plans seja undefined quando não há múltiplos planos
  const customReset = (data?: any) => {
    const resetData = {
      plan: undefined,
      plans: undefined,
      plan_slug_integration: data?.plan_slug_integration || "",
      plan_slug_integration_list: data?.plan_slug_integration_list || "",
      price_integration: undefined,
    };
    formMethods.reset(resetData);
  };

  const methods = {
    ...formMethods,
    reset: customReset,
  };


  const handleSubmit = async (data: CreateMapIntegrationPlanFormValues) => {
    // Priorizar plans sobre plan
    const plans = (data.plans && Array.isArray(data.plans) && data.plans.length > 0) 
      ? data.plans 
      : (data.plan ? [data.plan] : []);
    
    const planSlugIntegration = (data.plan_slug_integration || "").trim();
    const planSlugIntegrationList = (data.plan_slug_integration_list || "").trim();

    if (plans.length === 0) {
      throw new Error("Selecione pelo menos um plano");
    }

    if (!planSlugIntegration && !planSlugIntegrationList) {
      throw new Error(translate("subscription_mapintegrationplan_admin.validation.plan_slug_integration"));
    }

    // Criar mapeamentos (em massa se múltiplos planos)
    const creations = plans.map((planId) =>
      createMapIntegrationPlanService({
        plan: planId,
        plan_slug_integration: planSlugIntegration || undefined,
        plan_slug_integration_list: planSlugIntegrationList || undefined,
        price_integration: undefined,
        price_from: data.price_from ?? null,
        price_to: data.price_to ?? null,
      } as any)
    );

    await Promise.all(creations);

    queryClient.invalidateQueries({
      queryKey: listMapIntegrationPlansQueryKey,
      refetchType: 'all',
    });

    const message = plans.length > 1 
      ? translate("subscription_mapintegrationplan_admin.success.bulk_create")
      : translate("subscription_mapintegrationplan_admin.success.create");

    open?.({
      type: "success",
      message,
    });

    modalRef.current?.close();
  };

  const [isPending, setIsPending] = useState(false);

  const handleSubmitWithLoading = async (data: CreateMapIntegrationPlanFormValues) => {
    setIsPending(true);
    try {
      await handleSubmit(data);
    } finally {
      setIsPending(false);
    }
  };

  return {
    methods,
    mutateAsync: handleSubmitWithLoading,
    isPending,
  };
}
